import { useCallback, useState } from 'react';

import { apiService } from '../../services';
import type {
  AdhesionDetailResponseDTO,
  AdhesionListRequest,
  AdhesionListResponse,
  AdhesionListResponseDTO,
  DownloadAdhesionDetailRequestDTO,
} from '../../types/previdencia/previdenciaType';

export interface UsePrevidenciaAdhesionReturn {
  adhesions: AdhesionListResponseDTO[];
  adhesionDetail: AdhesionDetailResponseDTO | null;
  isLoading: boolean;
  error: string | null;
  totalPages: number;
  totalElements: number;
  currentPage: number;
  clearError: () => void;
  listAdhesions: (params: AdhesionListRequest) => Promise<void>;
  getAdhesionDetail: (adhesionId: number) => Promise<void>;
  downloadAdhesionDetail: (request: DownloadAdhesionDetailRequestDTO) => Promise<void>;
  clearAdhesionDetail: () => void;
}

export const usePrevidenciaAdhesion = (): UsePrevidenciaAdhesionReturn => {
  const [adhesions, setAdhesions] = useState<AdhesionListResponseDTO[]>([]);
  const [adhesionDetail, setAdhesionDetail] = useState<AdhesionDetailResponseDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(1); // Pagination component uses 1-based page numbers

  const listAdhesions = useCallback(async (params: AdhesionListRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response: AdhesionListResponse = await apiService.adhesion.listAdhesions(params);
      setAdhesions(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
      setCurrentPage((response.pageNumber || 0) + 1); // Pagination component uses 1-based page numbers
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar adesões';

      // 404 (Not Found) não é um erro, apenas significa que não há resultados
      // Verifica se a mensagem indica "não encontrado" ou se é um erro real
      const isNotFound = errorMessage.toLowerCase().includes('não encontrado') ||
        errorMessage.toLowerCase().includes('nao encontrado') ||
        errorMessage.toLowerCase().includes('not found');

      if (isNotFound) {
        // Trata como resultado vazio, não como erro
        setAdhesions([]);
        setTotalPages(0);
        setTotalElements(0);
        setCurrentPage(1); // Pagination component uses 1-based page numbers
        setError(null); // Não define erro para 404
      } else {
        // Erro real - define erro e lança exceção
        setError(errorMessage);
        setAdhesions([]);
        setTotalPages(0);
        setTotalElements(0);
        setCurrentPage(1); // Pagination component uses 1-based page numbers
        throw err;
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getAdhesionDetail = useCallback(async (adhesionId: number): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const detail: AdhesionDetailResponseDTO = await apiService.adhesion.getAdhesionDetail(adhesionId);
      setAdhesionDetail(detail);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar detalhes da adesão';
      setError(errorMessage);
      setAdhesionDetail(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const downloadAdhesionDetail = useCallback(async (request: DownloadAdhesionDetailRequestDTO): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await apiService.adhesion.downloadAdhesionDetail(request);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao fazer download da adesão';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearAdhesionDetail = useCallback(() => {
    setAdhesionDetail(null);
  }, []);

  return {
    adhesions,
    adhesionDetail,
    isLoading,
    error,
    totalPages,
    totalElements,
    currentPage,
    clearError,
    listAdhesions,
    getAdhesionDetail,
    downloadAdhesionDetail,
    clearAdhesionDetail,
  };
};

